import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  AFTER_DROPOFF_STATIONS_HEADING,
  BOARDING_STATION,
  DROP_OFF_STATION,
  LINE_STOPS_LIST_LABEL,
  MIDDLE_STATIONS_HEADING,
  NO_PICKUP_OR_DROP_OFF,
  PLATFORM,
  PREVIOUS_STATIONS_HEADING,
  STATION_CODE,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eSpecialMessagesEffects } from 'src/app/modules/route-planner/constants/special-messages.constants';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { SpecialMessage } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/special-messages/special-message.interface';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';

@Component({
    selector: 'app-search-line-detailed-stops',
    templateUrl: './search-line-detailed-stops.component.html',
    styleUrls: ['./search-line-detailed-stops.component.scss'],
    standalone: false
})
export class SearchLineDetailedStopsComponent implements OnInit {
  @Input() stops: Stop[];
  @Input() boardingStationSeq: number;
  @Input() dropingStationSeq: number;
  @Input() selectedDate: Date | null = null;
  @Input() selectedTime: string | null = null;

  @Output() closeCardEvent = new EventEmitter<void>();

  stationCodeText: string
  boardingStationText: string
  dropOffStationText: string
  noPickupOrDropOffText: string
  platformText: string
  previousStationsText: string
  middleStationsText: string
  afterDropoffStationsText: string
  lineStopsListLabel: string
  constructor(
    private readonly languageService: LanguageService,
    private readonly queryParamsService: QueryParamsService
  ) { }

  ngOnInit(): void {
    this.initializeMultiLanguageTexts()
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.stationCodeText = STATION_CODE[language.language]
      this.boardingStationText = BOARDING_STATION[language.language]
      this.dropOffStationText = DROP_OFF_STATION[language.language]
      this.noPickupOrDropOffText = NO_PICKUP_OR_DROP_OFF[language.language]
      this.platformText = PLATFORM[language.language]
      this.previousStationsText =
        PREVIOUS_STATIONS_HEADING[language.language] ??
        PREVIOUS_STATIONS_HEADING['english']
      this.middleStationsText =
        MIDDLE_STATIONS_HEADING[language.language] ??
        MIDDLE_STATIONS_HEADING['english']
      this.afterDropoffStationsText =
        AFTER_DROPOFF_STATIONS_HEADING[language.language] ??
        AFTER_DROPOFF_STATIONS_HEADING['english']
      this.lineStopsListLabel =
        LINE_STOPS_LIST_LABEL[language.language] ??
        LINE_STOPS_LIST_LABEL['english']
    })
  }

  /** Stops before selected boarding (greyed in UI). */
  get previousStops(): Stop[] {
    if (!this.stops?.length || this.boardingStationSeq == null) {
      return [];
    }
    return this.stops.filter((s) => s.stopSequence < this.boardingStationSeq);
  }

  /** Stops from boarding through drop-off (main segment). */
  get middleStops(): Stop[] {
    if (
      !this.stops?.length ||
      this.boardingStationSeq == null ||
      this.dropingStationSeq == null
    ) {
      return [];
    }
    return this.stops.filter(
      (s) =>
        s.stopSequence >= this.boardingStationSeq &&
        s.stopSequence <= this.dropingStationSeq
    );
  }

  /** Stops after selected drop-off (greyed in UI). */
  get afterDropoffStops(): Stop[] {
    if (!this.stops?.length || this.dropingStationSeq == null) {
      return [];
    }
    return this.stops.filter((s) => s.stopSequence > this.dropingStationSeq);
  }

  isStopCanceled(stop: Stop): boolean {
    if (!stop?.specialMessages || stop.specialMessages.length === 0) {
      return false;
    }
    
    const checkDate = this.getCheckDateTime();
    
    return stop.specialMessages.some(
      (message: SpecialMessage) => {
        const hasCancelEffect = 
          message.effect.id === eSpecialMessagesEffects.NO_SERVICE ||
          message.effect.id === eSpecialMessagesEffects.DETOUR ||
          message.effect.id === eSpecialMessagesEffects.STOP_MOVED;
        
        if (!hasCancelEffect) {
          return false;
        }
        
        // Check if message is active for the selected date/time
        return this.isMessageActive(message, checkDate);
      }
    );
  }

  private getCheckDateTime(): Date {
    if (this.selectedDate && this.selectedTime) {
      const checkDate = new Date(this.selectedDate);
      const [hours, minutes] = this.selectedTime.split(':').map(Number);
      checkDate.setHours(hours, minutes, 0, 0);
      return checkDate;
    }
    // If no date/time selected, use current date/time
    return new Date();
  }

  private isMessageActive(message: SpecialMessage, checkDate: Date): boolean {
    const fromDate = new Date(message.fromDate);
    const toDate = new Date(message.toDate);
    
    // Set time to start of day for fromDate and end of day for toDate
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(23, 59, 59, 999);
    
    return checkDate >= fromDate && checkDate <= toDate;
  }

  pickStop(stopCode: number) {
    this.closeCardEvent.emit();
    const params = {
      tab: eTab.STATIONS,
      stop: stopCode,
      routeId: null,
      oppositeRouteId: null,
      line: null,
      boardingStation: null,
      dropingStation: null,
      saveParams: true,
    };
    this.queryParamsService.setQueryParams(params);
  }

  getStopName(stop: Stop): string {
    return stop.name[this.languageService.getSelectedLanguage().symbol] || stop.name.he || stop.name || '';
  }
}
